<template>
  <ul class="user-menu-icons">
    <!-- Notifications -->
    <!-- Single icon -->
    <li
      :class="{
        'single-icon': true,
        'user-menu-icons__single-icon': true,
      }"
    >
      <a class="single-icon__link" :href="helpPageUrl" target="_blank">
        <div
          :class="{
            'single-icon__icon-itself': true,
          }"
          v-html="questionMarkIcon"
        ></div>
      </a>
    </li>
    <!-- Chat & tasks -->
    <li
      ref="chatMenuItemRef"
      :class="{
        'single-icon': true,
        'single-icon--active': isChatPanelOpen,
        'single-icon--deactivates-with-delay': !isChatPanelOpen,
        'user-menu-icons__single-icon': true,
      }"
      @click="onClickChat"
    >
      <div
        v-if="useChat().unreadCount"
        :class="{
          'counter-snippet': true,
          'single-icon__counter-snippet': true,
        }"
      >
        <div
          :class="{
            'counter-snippet__inner': true,
            'counter-snippet__inner--dark': isChatPanelOpen,
            'counter-snippet__inner--deactivates-with-delay': !isChatPanelOpen,
          }"
        >
          {{ useChat().unreadCount }}
        </div>
      </div>
      <div
        :class="{
          'single-icon__icon-itself': true,
          'single-icon__icon-itself--dark': isChatPanelOpen,
          'single-icon__icon-itself--deactivates-with-delay': !isChatPanelOpen,
        }"
        v-html="chatIcon"
      ></div>

      <teleport v-if="hasMounted" to="#portal-target">
        <!-- Chat panel -->
        <chat-panel
          :class="{
            'chat-panel': true,
            'chat-panel--closed': !isChatPanelOpen,
          }"
          :is-open="isChatPanelOpen"
          @click-outside="clickChatPanelOutside"
          @close-icon-click="onChatPanelClose"
        /><!-- / Chat panel -->
      </teleport>
    </li>
    <!-- / Chat & tasks -->
    <li
      ref="notificationsMenuItemRef"
      :class="{
        'single-icon': true,
        'single-icon--active': isNotificationsPanelOpen,
        'single-icon--deactivates-with-delay': !isNotificationsPanelOpen,
        'user-menu-icons__single-icon': true,
      }"
      @click="onClickNotifications"
    >
      <!-- Counter snippet -->
      <div
        v-if="useNotifications().numberOfUnreadNotifications"
        :class="{
          'counter-snippet': true,
          'single-icon__counter-snippet': true,
        }"
      >
        <div
          :class="{
            'counter-snippet__inner': true,
            'counter-snippet__inner--dark': isNotificationsPanelOpen,
            'counter-snippet__inner--deactivates-with-delay': !isNotificationsPanelOpen,
          }"
        >
          {{ useNotifications().numberOfUnreadNotifications }}
        </div>
      </div>
      <!-- / Counter snippet -->
      <div
        :class="{
          'single-icon__icon-itself': true,
          'single-icon__icon-itself--dark': isNotificationsPanelOpen,
          'single-icon__icon-itself--deactivates-with-delay': !isNotificationsPanelOpen,
        }"
        v-html="bellIcon"
      ></div>

      <!-- Loading animation bar -->
      <div
        v-if="useNotifications().hasInProgressNotifications"
        :class="{
          'loading-animation-bar': true,
          'loading-animation-bar--dark': isNotificationsPanelOpen,
          'loading-animation-bar--deactivates-with-delay': !isNotificationsPanelOpen,
          'single-icon__loading-animation-bar': true,
        }"
      >
        <div
          :class="{
            'loading-animation-bar__moving-bar': true,
            'loading-animation-bar__moving-bar--dark': isNotificationsPanelOpen,
            'loading-animation-bar__moving-bar--deactivates-with-delay': !isNotificationsPanelOpen,
          }"
        ></div>
      </div>
      <!-- / Loading animation bar -->

      <teleport v-if="hasMounted" to="#portal-target">
        <!-- Notifications panel -->
        <notifications-panel
          ref="notificationsPanelRef"
          :class="{
            'notifications-panel': true,
            'notifications-panel--closed': !isNotificationsPanelOpen,
          }"
          :is-open="isNotificationsPanelOpen"
          @click-outside="clickPanelOutside"
          @close-icon-click="onNotificationsPanelClose"
        /><!-- / Notifications panel -->
      </teleport>
    </li>
    <!-- / Single icon -->
    <!-- / Notifications -->
  </ul>
</template>

<script lang="ts" setup>
import NotificationsPanel from '@components/notifications/notifications-panel.vue';
import ChatPanel from '@components/chat/chat-panel.vue';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useNotifications } from '@/store/notifications';
import { useChat } from '@/store/chat';
import bellIcon from '@icons/other2/bell.svg';
import chatIcon from '@icons/filters-icons/comment-outlined.svg';
import questionMarkIcon from '@icons/other/question-mark-circle.svg';
import { useMounted } from '@lib/use-mounted';
import { useHelpPageUrl } from '@lib/help-link';
import { insideVueDevtools } from '@lib/events.ts';
import { isMac, useShortcut, useShortcutLayer } from '@lib/shortcuts';
import { chatPanel } from '@lib/chat-panel';
import { useAgentChat } from '@/store/agent-chat';
import { notificationsPanel } from '@lib/notifications-panel';

const hasMounted = useMounted();

const isNotificationsPanelOpen = notificationsPanel.isOpen;
const isChatPanelOpen = chatPanel.isOpen;

// Watched rather than handled in the toggles so shortcut-, palette- and
// phone-menu-triggered opens behave exactly like a click here. Only one
// right-side panel at a time — they share the same position — and opening
// notifications is what marks them read, whichever control did the opening;
// marking the chat seen is the panel's own business.
watch(isChatPanelOpen, (open) => {
  if (open) isNotificationsPanelOpen.value = false;
});

watch(isNotificationsPanelOpen, (open) => {
  if (!open) return;
  isChatPanelOpen.value = false;
  useNotifications().setAllAsViewed();
});

const onClickNotifications = () => {
  isNotificationsPanelOpen.value = !isNotificationsPanelOpen.value;
};

const chatMenuItemRef = ref<HTMLElement>();

const onClickChat = chatPanel.toggle;

const { register: registerChatShortcut } = useShortcutLayer(isChatPanelOpen, 'Chat');
registerChatShortcut('Escape', () => onChatPanelClose(), { description: 'Close chat' });
const { register: registerNotificationsShortcut } = useShortcutLayer(isNotificationsPanelOpen, 'Notifications');
registerNotificationsShortcut('Escape', () => (isNotificationsPanelOpen.value = false), {
  description: 'Close notifications',
});

const onChatPanelClose = () => {
  isChatPanelOpen.value = false;
};

// Plain Alt+C would hijack the Option layer on Mac, so Cmd is required there.
useShortcut(isMac ? 'meta+alt+c' : 'alt+c', () => chatPanel.toggleChat(), {
  allowInInput: true,
  description: 'Toggle chat',
  scope: 'Global',
});

// The agent is a tab of the same panel, so this opens it there rather than
// wherever the panel was last left — and closes again from the agent tab, which
// is what pressing the key that opened something a second time should do.
// Registered only where the server runs the agent chat: the tab is not there
// otherwise, and a key that opens an empty pane is worse than no key.
useShortcut(isMac ? 'meta+alt+a' : 'alt+a', () => chatPanel.toggleOn('agent'), {
  allowInInput: true,
  enabled: () => useAgentChat().enabled === true,
  description: 'Toggle the agent',
  scope: 'Global',
});

const clickChatPanelOutside = (event: MouseEvent) => {
  // When dev, don't close the panel on clicks for vue-devtools
  if (insideVueDevtools(event.target as Node)) {
    return;
  }

  // Clicks inside a teleported popper (the chat's user filter or @mention list)
  // are not "outside", and neither is another overlay sharing the portal — the
  // article slider above all, which the chat has to be able to sit over, since
  // that slider is exactly the page a message gets linked to. Use composedPath
  // (frozen at dispatch) rather than the live DOM — the clicked node may already
  // be detached by a Vue re-render by now.
  if (
    event
      .composedPath()
      .some(
        (node) =>
          node instanceof HTMLElement && (node.classList.contains('popper-wrapper') || node.id === 'portal-target')
      )
  ) {
    return;
  }

  if ((event.target as HTMLElement) !== chatMenuItemRef.value) {
    onChatPanelClose();
  }
};

const helpPageUrl = useHelpPageUrl();

const notificationsMenuItemRef = ref<HTMLElement>();
const clickPanelOutside = (event: MouseEvent) => {
  // When dev, don't close notifications on clicks for vue-devtools
  if (insideVueDevtools(event.target as Node)) {
    return;
  }

  // The phone menu carries its own bell, and that is the click that opened this
  // panel — without this it closes again on the very click that asked for it.
  // composedPath is frozen at dispatch, so it still holds the sheet even though
  // opening the panel closes it.
  if (event.composedPath().some((node) => node instanceof HTMLElement && node.classList.contains('nav-sheet'))) {
    return;
  }

  if ((event.target as HTMLElement) !== notificationsMenuItemRef.value) {
    onNotificationsPanelClose();
  }
};

const onNotificationsPanelClose = () => {
  if (isNotificationsPanelOpen.value) {
    isNotificationsPanelOpen.value = false;
    useNotifications().setAllAsViewed();
  }
};
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';
@import '@styles/media-queries.scss';

// Notifications panel --------------------------------------------------------
.notifications-panel {
  position: fixed;
  inset: 7rem 0 auto auto;
  z-index: $zi-notifications;
  transform: translateX(0);
  transition: transform 0.2s ease;

  &--closed {
    transform: translateX(100%);
  }

  @media print {
    display: none;
  }
}

// TODO: convert to rem values AFTER complete header css overhaul
@media (max-width: $desktop-width) {
  .notifications-panel {
    top: 5.6rem;
  }
}

@media (max-width: $tablet-p-width) {
  .notifications-panel {
    top: 4.9rem;
  }
}

// Chat panel -----------------------------------------------------------------
.chat-panel {
  position: fixed;
  inset: 7rem 0 auto auto;
  // Under the navbar, like the article flyout. A shadow's blur bleeds in every
  // direction whatever its offset, so a panel stacked ABOVE the bar casts a soft
  // band back across the join and reads as a card floating below it. Going under
  // the bar lets it paint over that bleed. Not the flyout's own band, though:
  // level with it, the flyout wins on DOM order and covers the panel.
  z-index: $zi-side-panel;
  transform: translateX(0);
  // Width as well as the slide: the panel sets its own width and widens for the agent tab, and
  // a `transition` here naming only transform replaces that rule rather than adding to it.
  transition:
    transform 0.2s ease,
    width 0.2s ease;

  &--closed {
    transform: translateX(100%);
  }

  @media print {
    display: none;
  }
}

@media (max-width: $desktop-width) {
  .chat-panel {
    top: 5.6rem;
  }
}

@media (max-width: $tablet-p-width) {
  .chat-panel {
    top: 4.9rem;
  }
}

// Counter snippet ------------------------------------------------------------
.counter-snippet {
  width: 3rem;
  height: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;

  &__inner {
    height: 1.6rem;
    min-width: 1.6rem;
    flex-shrink: 0;
    padding: 3px 0.5rem;
    border-radius: 10rem;
    font:
      900 1rem/1rem 'Montserrat',
      sans-serif;
    color: $c-on-accent;
    white-space: nowrap;
    text-align: center;
    background: $c-accent;
    box-shadow: 0 0 0 2px $c-surface;
    transition:
      color $transition-fast ease,
      background $transition-fast ease;

    &--dark {
      color: $c-on-accent;
      background: $c-accent;
      box-shadow: 0 0 0 2px $c-ink;
    }

    &--deactivates-with-delay {
      transition-delay: 0.15s;
    }
  }
}

// Single icon ---------------------------------------------------------
.single-icon {
  width: 4.2rem;
  height: 4.2rem;
  // A sized flex item is still a shrinkable one. Without this the row gives the
  // buttons up first when the header tightens: measured 42px -> 31.5px at 1120,
  // which squashes the round hit area into an ellipse and takes the glyph with
  // it. The buttons are a fixed cost; the slack has to come from elsewhere.
  flex-shrink: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 0;
  cursor: pointer;
  border-radius: 50%;
  background: rgba($c-ink, 0);
  transition:
    background $transition-smooth $ease-in-out-smooth,
    transform $transition-fast $ease-out-soft;
  user-select: none;

  &:hover {
    background: rgba($c-ink, 0.06);
  }
  &:active {
    transform: scale(0.92);
  }

  &::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: -1;
    border-radius: 50%;
    background: $c-ink;
    opacity: 0;
    transform: scale(0.6);
    transition:
      opacity $transition-fast $ease-in-out-smooth,
      transform $transition-fast $ease-spring;
    pointer-events: none;
  }

  &--active {
    &::before {
      opacity: 1;
      transform: scale(1);
    }
  }

  &--deactivates-with-delay {
    &::before {
      transition-delay: 0.1s;
    }
  }

  &__link {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__counter-snippet {
    position: absolute;
    inset: 0.3rem 0.1rem auto auto;
    pointer-events: none;
    z-index: 1;
  }

  // Out of the flex flow so the bell stays centred when the bar appears.
  // Offset is half the glyph plus a small gap.
  &__loading-animation-bar {
    top: calc(50% + 1.15rem);
    left: 50%;
    transform: translateX(-50%);
  }

  &__icon-itself {
    width: 1.9rem;
    height: 1.9rem;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;

    :deep(svg) {
      width: 100%;
      height: 100%;
      fill: $c-ink;
      // These glyphs are drawn edge to edge in their viewBoxes — the bell's ink
      // spans y 0.4..512.0 of a 512 box — so with the default overflow: hidden
      // the outermost row sits exactly on the clip boundary and gets shaved at
      // small sizes. Nothing here paints outside its viewBox, so letting it
      // overflow costs nothing and stops the shave.
      overflow: visible;
      transition: fill $transition-fast ease;
    }

    &--dark {
      :deep(svg) {
        fill: $c-surface;
      }
    }

    &--deactivates-with-delay {
      :deep(svg) {
        transition-delay: 0.15s;
      }
    }
  }
}

// notifications icons ------------------------------------------------------------
.loading-animation-bar {
  // Sits lower than when it shared the column with the bell, so it is
  // narrower to stay inside the round active background.
  width: 3.2rem;
  height: 3px;
  border-radius: 10rem;
  position: absolute;
  overflow: hidden;
  background: rgba($c-ink, 0.2);
  transition: background 0.01s ease;

  &--dark {
    background: rgba($c-surface, 0.4);
  }

  &--deactivates-with-delay {
    transition-delay: 0.15s;
  }

  &__moving-bar {
    width: 0;
    height: 100%;
    position: absolute;
    inset: 0 auto auto 0;
    animation-name: linePreloaderAnimation;
    animation-duration: 0.8s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;

    &::before {
      content: '';
      width: 3rem;
      height: 100%;
      border-radius: 10rem;
      position: absolute;
      inset: 0 auto auto -1rem;
      background: $c-ink;
      transition: background 0s ease;
    }

    &--dark {
      &::before {
        background: $c-surface;
      }
    }

    &--deactivates-with-delay {
      &::before {
        transition-delay: 0.1s;
      }
    }
  }

  @keyframes linePreloaderAnimation {
    0% {
      left: -1.5rem;
    }
    100% {
      left: calc(100% + 1.5rem);
    }
  }
}

.user-menu-icons {
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  list-style: none;

  &__single-icon {
  }
}

// Below 1200px the surrounding header collapses labels and trims spacing — the
// icons themselves can shrink to keep notifications + help inline with the
// rest. See navbar.vue for the matching responsive overrides.
@media (max-width: #{$desktop-width - 1px}) {
  .single-icon {
    // Width AND height. Trimming only the width left the box at 3.6 x 4.2rem,
    // and border-radius: 50% on a non-square box is an ellipse, not a circle:
    // measured 31.5 x 36.75 at 1180, so the hover pill and the ::before active
    // disc both pinched the glyph horizontally. That was the cropping.
    width: 3.6rem;
    height: 3.6rem;

    // 1.6, not 1.8. The button drops 4.2 -> 3.6rem here, so holding the glyph
    // at 1.8 raised it from 45% of the button to 50% — the glyphs got
    // proportionally BIGGER as the header got smaller, which is why they read
    // as cropped in this band and not on desktop. 1.6/3.6 restores the 45%.
    &__icon-itself {
      width: 1.6rem;
      height: 1.6rem;
    }

    // The bell's loading bar is sized against the 4.2rem box; at 3.6rem it
    // would run the full width of the button, edge to edge.
    .loading-animation-bar {
      width: 2.6rem;
    }

    &__loading-animation-bar {
      top: calc(50% + 1rem);
    }
  }
}
</style>
